import { useMemo, useRef } from "react"
import type { ReactNode } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@aix/ui"
import { BarChart3, Database, FileClock, RefreshCw } from "lucide-react"

import type { ReportingSeoData } from "@aix/aixinternal-shared/reporting"
import type { BusinessReportInput } from "@aix/aixinternal-shared/businessReporting"
import { EBusinessReportKind } from "@aix/aixinternal-shared/businessReporting"
import { businessReportModuleTables, deriveBusinessReport } from "@aix/aixinternal-shared/businessReportingView"
import { ALL_REPORT_PRODUCTS, deriveSeoReport, seoTrendGeometry } from "@aix/aixinternal-shared/reportingView"
import { trpc } from "../../lib/trpc"
import { useDebouncedQueryInput } from "../../lib/filters/useUrlFilters"
import { OperationalReport } from "./OperationalReport"
import { ReportsSetupPanel } from "./ReportsSetupPanel"
import { WeeklyBriefPanel } from "./WeeklyBriefPanel"
import { PageGroupReport } from "./PageGroupReport"

interface ReportsSearch {
  companyId?: string
  product?: string
  start?: string
  end?: string
  tab?: string
  snapshot?: string
  kind?: string
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const DAY_MS = 86_400_000

function isValidUuid(value: string | undefined): value is string {
  return Boolean(value && UUID_PATTERN.test(value))
}

function isValidDate(value: string | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
}

function pacificDate(now = new Date()): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(now)
      .map((part) => [part.type, part.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}`
}

function dateOffset(date: string, offset: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + offset * DAY_MS).toISOString().slice(0, 10)
}

function formatNumber(value: number | null): string {
  return value === null ? "Unknown" : new Intl.NumberFormat().format(value)
}

function formatPercent(value: number | null): string {
  return value === null ? "Unknown" : `${(value * 100).toFixed(1)}%`
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(
    new Date(`${value}T00:00:00Z`),
  )
}

function DataState({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "error" | "success" }) {
  const toneClass =
    tone === "error"
      ? "border-destructive/50 text-destructive"
      : tone === "success"
        ? "border-primary/40 text-primary"
        : "border-border text-muted-foreground"
  return (
    <div className={`rounded-md border bg-card px-4 py-3 text-sm ${toneClass}`} aria-live="polite">
      {children}
    </div>
  )
}

export function BusinessReportEdition({ input }: { input: BusinessReportInput }) {
  const view = deriveBusinessReport(input)
  return (
    <section className="space-y-4" aria-label={`${input.reportKind} business report`}>
      <Card>
        <CardHeader>
          <CardTitle className="capitalize">{input.reportKind} report</CardTitle>
          <CardDescription>
            {input.period.start} → {input.period.end} · definition v{input.definitionVersion} ·{" "}
            {view.complete ? "Complete" : "Incomplete"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {view.findings.length ? (
            view.findings.map((finding) => (
              <div key={finding.id} className="rounded-md border border-border p-3">
                <h3 className="font-medium text-foreground">{finding.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{finding.observation}</p>
                <p className="mt-2 text-sm">Next: {finding.proposedAction}</p>
              </div>
            ))
          ) : (
            <DataState>No evidence-backed finding was produced for this frozen window.</DataState>
          )}
        </CardContent>
      </Card>
      {view.modules.map((module) => (
        <Card key={module.domain}>
          <CardHeader>
            <CardTitle className="capitalize">{module.domain}</CardTitle>
            <CardDescription>{module.reason ?? module.evidence.coverage.detail}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <DataState tone={module.status === "available" ? "success" : "muted"}>
              {module.status} · observed {module.evidence.coverage.observed ?? "Unknown"} of{" "}
              {module.evidence.coverage.expected ?? "Unknown"}
            </DataState>
            {businessReportModuleTables(module).map((table) => (
              <div key={table.title} className="overflow-x-auto">
                <h3 className="mb-2 text-sm font-medium">{table.title}</h3>
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {table.columns.map((column) => (
                        <th key={column} className="px-2 py-2 font-medium">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((row, rowIndex) => (
                      <tr key={`${table.title}-${rowIndex}`} className="border-b border-border/60 last:border-0">
                        {row.map((cell, cellIndex) => (
                          <td key={`${cellIndex}-${cell}`} className="px-2 py-2">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </section>
  )
}

function Metric({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-md border border-border bg-secondary/30 p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold text-foreground">{value}</div>
      {detail ? <div className="mt-1 text-xs text-muted-foreground">{detail}</div> : null}
    </div>
  )
}

function TrendChart({
  points,
  metric,
  label,
  weekly = false,
}: {
  points: ReturnType<typeof deriveSeoReport>[number]["points"]
  metric: "clicks" | "impressions"
  label: string
  weekly?: boolean
}) {
  const geometry = seoTrendGeometry(points, metric)
  const firstDate = points[0]?.date
  const lastDate = points.at(-1)?.date
  return (
    <div>
      <svg
        className="h-auto w-full text-primary"
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        role="img"
        aria-label={label}
      >
        <title>{label}</title>
        <desc>
          Independent product scale. Missing or incomplete buckets break the line.{" "}
          {weekly
            ? "Dates mark Monday week starts; only complete seven-day totals are plotted."
            : "Each point is one source day."}{" "}
          Exact values are available in the table below.
        </desc>
        <line
          x1={geometry.left}
          x2={geometry.left}
          y1={geometry.top}
          y2={geometry.baseline}
          className="stroke-border"
          strokeWidth="1"
        />
        <line
          x1={geometry.left}
          x2={geometry.width - 15}
          y1={geometry.top}
          y2={geometry.top}
          className="stroke-border/50"
          strokeWidth="1"
          strokeDasharray="3 3"
        />
        <line
          x1={geometry.left}
          x2={geometry.width - 15}
          y1={geometry.baseline}
          y2={geometry.baseline}
          className="stroke-border"
          strokeWidth="1"
        />
        {geometry.segments.map((segment, index) => (
          <path
            key={`${metric}-${index}`}
            d={segment}
            fill="none"
            className="stroke-current"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {geometry.coordinates.map((point) =>
          point.y === null ? null : (
            <circle key={`${metric}-${point.date}`} cx={point.x} cy={point.y} r="3" className="fill-primary" />
          ),
        )}
        <text
          x={geometry.left - 5}
          y={geometry.top + 4}
          textAnchor="end"
          className="fill-muted-foreground"
          fontSize="11"
        >
          {formatNumber(geometry.max)}
        </text>
        <text
          x={geometry.left - 5}
          y={geometry.baseline + 4}
          textAnchor="end"
          className="fill-muted-foreground"
          fontSize="11"
        >
          0
        </text>
        {firstDate ? (
          <text
            x={geometry.left}
            y={geometry.height - 8}
            textAnchor="start"
            className="fill-muted-foreground"
            fontSize="11"
          >
            {formatDate(firstDate)}
          </text>
        ) : null}
        {lastDate ? (
          <text
            x={geometry.width - 15}
            y={geometry.height - 8}
            textAnchor="end"
            className="fill-muted-foreground"
            fontSize="11"
          >
            {formatDate(lastDate)}
          </text>
        ) : null}
      </svg>
      <p className="text-xs text-muted-foreground">
        {points.filter((point) => point[metric] !== null).length}/{points.length}{" "}
        {weekly ? "complete Monday–Sunday weeks · dates mark week starts" : "days observed"} · independent product scale
      </p>
    </div>
  )
}

function TrendTable({ points }: { points: ReturnType<typeof deriveSeoReport>[number]["points"] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm" aria-label="SEO daily trend data">
        <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-2 py-2 font-medium">Date</th>
            <th className="px-2 py-2 font-medium">Clicks</th>
            <th className="px-2 py-2 font-medium">Impressions</th>
          </tr>
        </thead>
        <tbody>
          {points.map((point) => (
            <tr key={point.date} className="border-b border-border/60 last:border-0">
              <td className="px-2 py-2 text-foreground">{formatDate(point.date)}</td>
              <td className="px-2 py-2 text-foreground">{formatNumber(point.clicks)}</td>
              <td className="px-2 py-2 text-foreground">{formatNumber(point.impressions)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function WeeklyTrendTable({ points }: { points: ReturnType<typeof deriveSeoReport>[number]["weeklyPoints"] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm" aria-label="SEO weekly trend data">
        <thead>
          <tr>
            <th className="p-2">Pacific week</th>
            <th className="p-2">Observed days</th>
            <th className="p-2">Clicks</th>
            <th className="p-2">Impressions</th>
          </tr>
        </thead>
        <tbody>
          {points.map((week) => (
            <tr key={week.date} className="border-t border-border">
              <td className="p-2">
                {week.date}–{week.end}
              </td>
              <td className="p-2">{week.observedDays}/7</td>
              <td className="p-2">{formatNumber(week.clicks)}</td>
              <td className="p-2">{formatNumber(week.impressions)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!points.length ? <p>No completed calendar week in the selected range.</p> : null}
    </div>
  )
}

function PageMovers({ report }: { report: ReturnType<typeof deriveSeoReport>[number] }) {
  const changes = report.pageChanges
  const groups = [
    ["Winners", changes.winners],
    ["Decliners", changes.decliners],
  ] as const
  return (
    <details className="rounded-md border border-border p-3">
      <summary className="cursor-pointer text-sm font-medium">Page winners and decliners</summary>
      <p className="my-3 text-sm text-muted-foreground">
        {report.currentDates[0]}–{report.currentDates.at(-1)} vs {report.priorDates[0] ?? "Unknown"}–
        {report.priorDates.at(-1) ?? "Unknown"}. Complete page exports: current {changes.currentDays}/7, prior{" "}
        {changes.priorDays}/7. Changes describe returned page exports, not property totals or indexation. An absent page
        contributes zero returned clicks only when both exports are complete.
      </p>
      {changes.comparable ? (
        groups.map(([title, rows]) => (
          <section key={title} className="mt-4">
            <h3 className="text-sm font-medium">{title} · up to five by absolute click change</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr>
                    <th className="p-2">Page</th>
                    <th className="p-2">Prior clicks</th>
                    <th className="p-2">Current clicks</th>
                    <th className="p-2">Change</th>
                    <th className="p-2">Prior / current impressions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.entity} className="border-t border-border">
                      <td className="max-w-sm break-all p-2">{row.entity}</td>
                      <td className="p-2">{formatNumber(row.priorClicks)}</td>
                      <td className="p-2">{formatNumber(row.currentClicks)}</td>
                      <td className="p-2">
                        {row.clickChange > 0 ? "+" : ""}
                        {formatNumber(row.clickChange)}
                        {row.percentChange === null ? " · no prior percentage baseline" : ""}
                      </td>
                      <td className="p-2">
                        {formatNumber(row.priorImpressions)} / {formatNumber(row.currentImpressions)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!rows.length ? (
                <p className="text-sm text-muted-foreground">No returned pages changed in this direction.</p>
              ) : null}
            </div>
          </section>
        ))
      ) : (
        <DataState>
          Comparison unavailable: requires two complete seven-day page exports with the same source, property and query
          version.
        </DataState>
      )}
    </details>
  )
}

function ProductReport({ report }: { report: ReturnType<typeof deriveSeoReport>[number] }) {
  const currentPartial = !report.current.complete
  const priorPartial = !report.prior.complete
  const failurePreview = report.latestAttempts.slice(0, 5)
  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{report.product.name}</CardTitle>
            <CardDescription>
              {report.product.hosts.join(", ")} · data begins {formatDate(report.product.includedFrom)}
            </CardDescription>
          </div>
          <div
            className={`rounded-full border px-3 py-1 text-xs ${currentPartial ? "border-border text-muted-foreground" : "border-primary/40 text-primary"}`}
          >
            {currentPartial ? "Partial current window" : "Current window complete"} · {report.current.observedDays}/
            {report.current.expectedDays} days observed
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-4">
          <Metric
            label="Current clicks"
            value={formatNumber(report.current.clicks)}
            detail={
              currentPartial
                ? `Partial total · ${report.current.observedDays}/${report.current.expectedDays} days observed`
                : "Complete seven-day window"
            }
          />
          <Metric
            label="Current impressions"
            value={formatNumber(report.current.impressions)}
            detail={
              currentPartial
                ? `Partial total · ${report.current.observedDays}/${report.current.expectedDays} days observed`
                : "Complete seven-day window"
            }
          />
          <Metric label="CTR" value={formatPercent(report.current.ctr)} detail="derived from observed facts" />
          <Metric
            label="Prior clicks"
            value={formatNumber(report.prior.clicks)}
            detail={
              priorPartial
                ? `Partial total · ${report.prior.observedDays}/${report.prior.expectedDays} days observed`
                : "Complete comparison window"
            }
          />
        </div>
        <div className="grid gap-5 lg:grid-cols-2">
          <section
            aria-labelledby={`${report.product.key}-clicks-heading`}
            className="rounded-md border border-border p-3"
          >
            <h3 id={`${report.product.key}-clicks-heading`} className="mb-2 text-sm font-medium">
              Weekly organic clicks
            </h3>
            <TrendChart
              points={report.weeklyPoints}
              metric="clicks"
              label={`${report.product.name} weekly organic clicks`}
              weekly
            />
          </section>
          <section
            aria-labelledby={`${report.product.key}-impressions-heading`}
            className="rounded-md border border-border p-3"
          >
            <h3 id={`${report.product.key}-impressions-heading`} className="mb-2 text-sm font-medium">
              Weekly search impressions
            </h3>
            <TrendChart
              points={report.weeklyPoints}
              metric="impressions"
              label={`${report.product.name} weekly search impressions`}
              weekly
            />
          </section>
        </div>
        <p className="text-sm text-muted-foreground">
          Headline source period {report.currentDates[0]}–{report.currentDates.at(-1)} · Pacific dates.{" "}
          {report.clickChange === null
            ? "Comparable complete windows unavailable."
            : `${report.clickChange >= 0 ? "+" : ""}${formatNumber(report.clickChange)} clicks vs prior seven days.`}{" "}
          Weekly charts show completed calendar weeks; partial weeks remain gaps.
        </p>
        <PageGroupReport report={report} />
        <PageMovers report={report} />
        <details className="rounded-md border border-border p-3">
          <summary className="cursor-pointer text-sm font-medium">
            Weekly totals, daily trends and landing pages
          </summary>
          <WeeklyTrendTable points={report.weeklyPoints} />
          <div className="my-4 grid gap-5 lg:grid-cols-2">
            <section>
              <h3 className="text-sm font-medium">Daily organic clicks</h3>
              <TrendChart
                points={report.points.slice(-28)}
                metric="clicks"
                label={`${report.product.name} daily organic clicks`}
              />
            </section>
            <section>
              <h3 className="text-sm font-medium">Daily search impressions</h3>
              <TrendChart
                points={report.points.slice(-28)}
                metric="impressions"
                label={`${report.product.name} daily search impressions`}
              />
            </section>
          </div>
          <TrendTable points={report.points} />
          <p className="my-3 text-sm text-muted-foreground">
            Landing-page export: {report.pageCoverage.observedDays}/{report.pageCoverage.expectedDays} complete days.
            Page exports and property totals are different aggregations.
          </p>
          <section aria-labelledby={`${report.product.key}-pages-heading`}>
            <h3 id={`${report.product.key}-pages-heading`} className="mb-2 text-sm font-medium">
              Top pages in the current window
            </h3>
            {report.topPages.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 font-medium">Page</th>
                      <th className="px-2 py-2 font-medium">Clicks</th>
                      <th className="px-2 py-2 font-medium">Impressions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.topPages.map((page) => (
                      <tr key={page.entity} className="border-b border-border/60 last:border-0">
                        <td className="max-w-[30rem] truncate px-2 py-2 text-foreground" title={page.entity}>
                          {page.entity}
                        </td>
                        <td className="px-2 py-2 text-foreground">{formatNumber(page.clicks)}</td>
                        <td className="px-2 py-2 text-foreground">{formatNumber(page.impressions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <DataState>No page-level observations are persisted for this window.</DataState>
            )}
          </section>
        </details>
        {report.latestAttempts.length ? (
          <DataState tone="error">
            Latest source attempts include unavailable or partial days ({report.latestAttempts.length} total). Showing{" "}
            {failurePreview.length}:{" "}
            {failurePreview.map((attempt) => `${attempt.date} ${attempt.grain} (${attempt.status})`).join(", ")}
            {report.latestAttempts.length > failurePreview.length ? "…" : ""}
          </DataState>
        ) : null}
      </CardContent>
    </Card>
  )
}

export default function ReportsScreen() {
  const search = useSearch({ strict: false }) as ReportsSearch
  const navigate = useNavigate()
  const utils = trpc.useUtils()
  const today = pacificDate()
  const defaultEnd = dateOffset(today, -3)
  const defaultStart = dateOffset(defaultEnd, -55)
  const companyId = isValidUuid(search.companyId) ? search.companyId : ""
  const start = search.start ?? defaultStart
  const end = search.end ?? defaultEnd
  const dateRange = useMemo(() => ({ start, end }), [start, end])
  const { debouncedInput: queryDates, isDebouncing: datesPending } = useDebouncedQueryInput(dateRange)
  const validRange = isValidDate(start) && isValidDate(end) && start <= end && dateOffset(start, 119) >= end
  const tab = search.tab === "archive" ? "archive" : "live"
  const reportKinds = Object.values(EBusinessReportKind)
  const reportKind = reportKinds.includes(search.kind as EBusinessReportKind)
    ? (search.kind as EBusinessReportKind)
    : EBusinessReportKind.EXECUTIVE
  const updateSearch = (patch: Partial<ReportsSearch>) =>
    navigate({ to: "/reports", search: (current: Record<string, unknown>) => ({ ...current, ...patch }) })

  const companiesQuery = trpc.companies.list.useQuery({ status: "active" })
  const companies = companiesQuery.data?.companies ?? []
  const selectedCompany = companies.find((company) => company.id === companyId)
  const inventoryQuery = trpc.reporting.inventory.useQuery({ companyId }, { enabled: Boolean(selectedCompany) })
  const products = inventoryQuery.data?.products ?? []
  const selectedProduct =
    search.product && products.some((product) => product.key === search.product) ? search.product : undefined
  const invalidProduct = Boolean(search.product && products.length && !selectedProduct)
  const productKeys = selectedProduct
    ? [selectedProduct]
    : products.filter((product) => product.active).map((product) => product.key)
  const seoQuery = trpc.reporting.weekly.useQuery(
    { companyId, range: { ...queryDates, productKeys } },
    {
      enabled: Boolean(
        selectedCompany && validRange && !datesPending && !invalidProduct && productKeys.length && tab === "live",
      ),
    },
  )
  const compositionRequest = {
    companyId,
    reportKind,
    definitionVersion: 1,
    productKeys,
    period: { ...queryDates, timezone: "America/Los_Angeles" },
  }
  const editionRef = useRef<{ key: string; id: string }>()
  const editionKey = JSON.stringify(compositionRequest)
  if (editionRef.current?.key !== editionKey) editionRef.current = { key: editionKey, id: crypto.randomUUID() }
  const editionId = editionRef.current.id
  const businessQuery = trpc.reporting.compose.useQuery(compositionRequest, {
    enabled: Boolean(
      selectedCompany && validRange && !datesPending && !invalidProduct && productKeys.length && tab === "live",
    ),
  })
  const createSnapshot = trpc.reporting.createBusinessSnapshot.useMutation({
    onSuccess: () => {
      void utils.reporting.snapshots.invalidate({ companyId })
    },
  })
  const snapshotsQuery = trpc.reporting.snapshots.useQuery(
    { companyId, limit: 30, reportKind: search.kind === "all" ? undefined : reportKind },
    { enabled: Boolean(selectedCompany && tab === "archive") },
  )
  const snapshotId = isValidUuid(search.snapshot) ? search.snapshot : ""
  const snapshotQuery = trpc.reporting.snapshot.useQuery(
    { companyId, id: snapshotId },
    { enabled: Boolean(selectedCompany && tab === "archive" && snapshotId) },
  )
  const snapshotInput = snapshotQuery.data?.input
  const archivedBusiness = snapshotInput?.schemaVersion === 2 ? (snapshotInput as BusinessReportInput) : undefined
  const archivedLegacy = snapshotInput?.schemaVersion === 1 ? (snapshotInput as ReportingSeoData) : undefined
  const businessData = tab === "archive" ? archivedBusiness : businessQuery.data
  const reportData: ReportingSeoData | undefined = tab === "archive" ? archivedLegacy : seoQuery.data
  const derivedReports = useMemo(
    () =>
      reportData
        ? deriveSeoReport(reportData).filter((report) => !selectedProduct || report.product.key === selectedProduct)
        : [],
    [reportData, selectedProduct],
  )

  return (
    <main className="min-h-screen bg-background p-4 text-foreground sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm text-primary">
              <BarChart3 size={18} /> AIX Growth
            </div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight">Reports</h1>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              One read-only reporting surface for the shared AIX Weekly data contract. SEO is the first connected
              channel; unknown source days stay unknown.
            </p>
          </div>
          <div className="flex gap-2">
            {tab === "live" && businessData ? (
              <Button
                variant="outline"
                disabled={createSnapshot.isLoading || datesPending || !validRange}
                onClick={() => createSnapshot.mutate({ ...compositionRequest, editionId })}
              >
                {createSnapshot.isLoading ? "Saving…" : "Save snapshot"}
              </Button>
            ) : null}
            <Button
              variant={tab === "live" ? "default" : "outline"}
              onClick={() => updateSearch({ tab: "live", snapshot: undefined })}
            >
              Live window
            </Button>
            <Button
              variant={tab === "archive" ? "default" : "outline"}
              onClick={() => updateSearch({ tab: "archive" })}
            >
              Archived snapshots
            </Button>
          </div>
        </header>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database size={18} /> Scope and source
            </CardTitle>
            <CardDescription>Select a company explicitly. Filters are URL-backed and shareable.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-5">
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Company</span>
              <Select
                value={companyId || undefined}
                onValueChange={(value) => updateSearch({ companyId: value, product: undefined, snapshot: undefined })}
              >
                <SelectTrigger aria-label="Company">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Product</span>
              <Select
                value={selectedProduct ?? ALL_REPORT_PRODUCTS}
                onValueChange={(value) => updateSearch({ product: value === ALL_REPORT_PRODUCTS ? undefined : value })}
                disabled={!products.length}
              >
                <SelectTrigger aria-label="Product">
                  <SelectValue placeholder="All products" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_REPORT_PRODUCTS}>All products</SelectItem>
                  {products.map((product) => (
                    <SelectItem key={product.key} value={product.key}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Report</span>
              <Select
                value={tab === "archive" && search.kind === "all" ? "all" : reportKind}
                onValueChange={(value) => updateSearch({ kind: value, snapshot: undefined })}
              >
                <SelectTrigger aria-label="Report kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tab === "archive" ? (
                    <SelectItem value="all">All editions · includes legacy AIX Weekly</SelectItem>
                  ) : null}
                  {reportKinds.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {kind[0]?.toUpperCase()}
                      {kind.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Start date</span>
              <Input
                type="date"
                value={start}
                onChange={(event) => updateSearch({ start: event.target.value })}
                aria-label="Report start date"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">End date</span>
              <Input
                type="date"
                value={end}
                onChange={(event) => updateSearch({ end: event.target.value })}
                aria-label="Report end date"
              />
            </label>
          </CardContent>
        </Card>
        {companiesQuery.isLoading ? <DataState>Loading active companies…</DataState> : null}
        {companiesQuery.error ? (
          <DataState tone="error">Could not load companies: {companiesQuery.error.message}</DataState>
        ) : null}
        {!companiesQuery.isLoading && !companiesQuery.error && !companies.length ? (
          <DataState>
            No active companies are available. Select or configure a company before reading reports.
          </DataState>
        ) : null}
        {search.companyId && !isValidUuid(search.companyId) ? (
          <DataState tone="error">
            The company URL filter is not a valid UUID. Choose a company from the selector.
          </DataState>
        ) : null}
        {isValidUuid(search.companyId) && !companiesQuery.isLoading && companies.length && !selectedCompany ? (
          <DataState tone="error">
            That company is not in the active company list. No report query was issued.
          </DataState>
        ) : null}
        {selectedCompany && inventoryQuery.isLoading ? <DataState>Loading reporting inventory…</DataState> : null}
        {selectedCompany && inventoryQuery.error ? (
          <DataState tone="error">Reporting inventory is unavailable: {inventoryQuery.error.message}</DataState>
        ) : null}
        {selectedCompany && inventoryQuery.data && !products.length ? (
          <DataState>
            No reporting products are configured for this company. This surface will not invent a product or zero
            metrics.
          </DataState>
        ) : null}
        {selectedCompany ? (
          <ReportsSetupPanel
            key={companyId}
            companyId={companyId}
            products={products}
            onSaved={() => {
              void utils.reporting.inventory.invalidate({ companyId })
              void utils.reporting.weekly.invalidate({ companyId })
            }}
          />
        ) : null}
        {selectedCompany && invalidProduct ? (
          <DataState tone="error">
            The product URL filter is not configured for this company. Choose a product from the selector.
          </DataState>
        ) : null}
        {!validRange ? <DataState tone="error">Choose a valid inclusive date range of 1–120 days.</DataState> : null}
        {selectedCompany && tab === "live" && inventoryQuery.data && products.length > 0 && !productKeys.length ? (
          <DataState>
            No active products are included. Choose an individual historical product to inspect its stored observations.
          </DataState>
        ) : null}
        {selectedCompany && tab === "live" && seoQuery.isFetching ? (
          <DataState>Loading persisted SEO and operational evidence…</DataState>
        ) : null}
        {selectedCompany && tab === "live" && seoQuery.error ? (
          <DataState tone="error">
            The reporting API could not read this window: {seoQuery.error.message}. Production data is not substituted.
          </DataState>
        ) : null}
        {selectedCompany && tab === "live" && businessQuery.isFetching ? (
          <DataState>Composing the {reportKind} evidence envelope…</DataState>
        ) : null}
        {selectedCompany && tab === "live" && businessQuery.error ? (
          <DataState tone="error">
            The {reportKind} report is unavailable: {businessQuery.error.message}
          </DataState>
        ) : null}
        {createSnapshot.error ? (
          <DataState tone="error">Snapshot was not saved: {createSnapshot.error.message}</DataState>
        ) : null}
        {tab === "archive" ? (
          <ArchivePanel
            snapshots={snapshotsQuery.data ?? []}
            isLoading={snapshotsQuery.isLoading}
            error={snapshotsQuery.error?.message}
            selectedId={snapshotId}
            onSelect={(id) => updateSearch({ snapshot: id })}
            snapshotLoaded={Boolean(snapshotQuery.data?.input)}
            snapshotLoading={snapshotQuery.isLoading}
            snapshotError={snapshotQuery.error?.message}
          />
        ) : null}
        {selectedCompany && tab === "live" && seoQuery.data && !seoQuery.data.days.length ? (
          <DataState>
            No persisted SEO observations exist for this window yet. This is a real empty state, not zero-filled data.
          </DataState>
        ) : null}
        {businessData ? <BusinessReportEdition input={businessData} /> : null}
        {reportData ? (
          <>
            <WeeklyBriefPanel data={reportData} productKey={selectedProduct} />
            <details className="rounded-md border border-border p-3">
              <summary className="cursor-pointer text-sm font-medium">Source bindings</summary>
              <Card>
                <CardHeader>
                  <CardTitle>Source coverage</CardTitle>
                  <CardDescription>
                    Shared reporting schema v{reportData.schemaVersion}; collection timestamps are preserved from AIX.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 md:grid-cols-2">
                  {reportData.sources
                    .filter((source) => !selectedProduct || source.productKey === selectedProduct)
                    .map((source) => (
                      <div key={source.key} className="rounded-md border border-border p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{source.key}</span>
                          <span className="text-xs text-muted-foreground">{source.provider}</span>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {source.provider === "gsc"
                            ? `GSC binding · ${source.externalId}`
                            : "Configured binding; catalog and other adapter coverage is shown in product sections."}
                        </p>
                      </div>
                    ))}
                </CardContent>
              </Card>
            </details>
            <section className="space-y-4" aria-label="Product SEO reports">
              {derivedReports.map((report) => (
                <div key={report.product.key} id={`product-${report.product.key}`} className="space-y-4">
                  <ProductReport report={report} />
                  {reportData.operations ? (
                    <OperationalReport
                      operations={reportData.operations}
                      productKey={report.product.key}
                      productName={report.product.name}
                    />
                  ) : null}
                </div>
              ))}
            </section>
          </>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {tab === "live" ? (
            <>
              <RefreshCw size={12} className="mr-1 inline" />
              Live view reads AIX persisted observations for {start}–{end}. It never calls GSC directly.
            </>
          ) : (
            <>
              <FileClock size={12} className="mr-1 inline" />
              Archive view reads immutable AIX snapshot inputs.
            </>
          )}
        </p>
      </div>
    </main>
  )
}

export function ArchivePanel({
  snapshots,
  isLoading,
  error,
  selectedId,
  onSelect,
  snapshotLoaded,
  snapshotLoading,
  snapshotError,
}: {
  snapshots: {
    id: string
    start: string
    end: string
    digest: string
    createdAt: string | Date
    schemaVersion?: number
    reportKind?: string | null
  }[]
  isLoading: boolean
  error?: string
  selectedId: string
  onSelect: (id: string) => void
  snapshotLoaded: boolean
  snapshotLoading: boolean
  snapshotError?: string
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Archived snapshots</CardTitle>
        <CardDescription>Immutable weekly inputs remain separate from the live read.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <DataState>Loading snapshots…</DataState>
        ) : error ? (
          <DataState tone="error">Could not load snapshots: {error}</DataState>
        ) : !snapshots.length ? (
          <DataState>No persisted snapshots are available yet.</DataState>
        ) : (
          <div className="grid gap-2">
            {snapshots.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => onSelect(item.id)}
                className={`flex items-center justify-between rounded-md border p-3 text-left transition-colors hover:bg-secondary/50 ${selectedId === item.id ? "border-primary bg-secondary/40" : "border-border"}`}
                aria-pressed={selectedId === item.id}
              >
                <span>
                  <span className="block font-medium text-foreground">
                    {item.start} → {item.end}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {item.reportKind ?? "Legacy AIX Weekly"} · schema {item.schemaVersion ?? 1}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Created {new Date(item.createdAt).toLocaleString()}
                  </span>
                </span>
                <span className="max-w-48 truncate text-xs text-muted-foreground">{item.digest}</span>
              </button>
            ))}
          </div>
        )}
        {selectedId && snapshotLoading ? <DataState>Loading selected snapshot…</DataState> : null}
        {snapshotError ? <DataState tone="error">Could not load selected snapshot: {snapshotError}</DataState> : null}
        {selectedId && snapshotLoaded ? (
          <DataState tone="success">
            Snapshot loaded. Product and trend sections below use its persisted input contract.
          </DataState>
        ) : null}
      </CardContent>
    </Card>
  )
}
